import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { openProtectedFile } from "@/lib/openFile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusPill } from "./DashboardLayout";
import { toast } from "sonner";
import { Award, IdCard, CheckCircle2, FileText, Check, X } from "lucide-react";

const TYPE_LABEL = {
  national_id: "National ID",
  passport: "Passport",
  driver_license: "Driver license",
  other: "Other",
};

function DocumentRow({ kind, doc, onReview, busy }) {
  const Icon = kind === "license" ? Award : IdCard;
  const label = kind === "license"
    ? "Medical license (Exequatur / Colegiatura)"
    : "Identity document";
  const reviewPath = kind === "license" ? "licenses" : "identity";
  const fileId = doc && (kind === "license" ? doc.license_document_id : doc.id_document_id);

  if (!doc) {
    return (
      <div className="py-3 flex items-start gap-3 border-t border-border first:border-t-0 first:pt-0">
        <Icon className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{label}</div>
          <div className="text-xs text-muted-foreground mt-0.5">Not uploaded yet.</div>
        </div>
        <StatusPill status="none" />
      </div>
    );
  }

  return (
    <div className="py-3 flex items-start gap-3 border-t border-border first:border-t-0 first:pt-0">
      <Icon className="w-4 h-4 text-[hsl(var(--accent))] shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
          <FileText className="w-3 h-3 shrink-0" />
          <span className="truncate max-w-[200px] sm:max-w-none">{doc.original_filename}</span>
          {kind === "id" && doc.document_type && (
            <span className="font-mono">· {TYPE_LABEL[doc.document_type] || doc.document_type}</span>
          )}
          <span>· {new Date(doc.created_at).toLocaleDateString()}</span>
        </div>
        {doc.notes && (
          <div className="text-xs italic text-muted-foreground mt-1">"{doc.notes}"</div>
        )}
        <button
          type="button"
          onClick={() => openProtectedFile(`/${reviewPath}/${fileId}/file`)}
          className="text-xs underline text-[hsl(var(--accent))] mt-1.5 inline-block"
          data-testid={`view-${reviewPath}-${fileId}`}
        >
          View file →
        </button>
      </div>
      <div className="shrink-0 flex flex-col items-end gap-1.5">
        <StatusPill status={doc.status} />
        {doc.status === "pending" && (
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => onReview(kind, doc, "approved")}
              disabled={busy}
              data-testid={`approve-${kind}-${fileId}`}
              title="Approve this document"
              className="h-7 w-7 grid place-items-center rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => onReview(kind, doc, "rejected")}
              disabled={busy}
              data-testid={`reject-${kind}-${fileId}`}
              title="Reject this document"
              className="h-7 w-7 grid place-items-center rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminVerifications() {
  const [groups, setGroups] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [notesByUser, setNotesByUser] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const [licAll, idAll] = await Promise.all([
        api.get("/licenses").then((r) => r.data),
        api.get("/identity").then((r) => r.data),
      ]);
      const map = new Map();
      const upsert = (key, patch) => {
        const cur = map.get(key) || {
          user_id: patch.user_id,
          user_email: patch.user_email,
          user_name: patch.user_name,
          license: null,
          id: null,
          latest: 0,
        };
        Object.assign(cur, patch.values || {});
        const ts = Math.max(cur.latest, new Date(patch.timestamp || 0).getTime() || 0);
        cur.latest = ts;
        map.set(key, cur);
      };
      const latestByUser = (list, kind) => {
        const m = new Map();
        for (const d of list) {
          const ts = new Date(d.created_at).getTime();
          const cur = m.get(d.user_id);
          if (!cur || ts > new Date(cur.created_at).getTime()) m.set(d.user_id, d);
        }
        for (const [, d] of m) {
          upsert(d.user_id, {
            user_id: d.user_id, user_email: d.user_email, user_name: d.user_name,
            timestamp: d.created_at,
            values: { [kind]: d },
          });
        }
      };
      latestByUser(licAll, "license");
      latestByUser(idAll, "id");

      let rows = Array.from(map.values()).sort((a, b) => b.latest - a.latest);
      if (filter === "pending") {
        rows = rows.filter(
          (g) => g.license?.status === "pending" || g.id?.status === "pending"
        );
      } else if (filter === "incomplete") {
        rows = rows.filter(
          (g) => !g.license || !g.id || g.license.status !== "approved" || g.id.status !== "approved"
        );
      } else if (filter === "approved") {
        rows = rows.filter(
          (g) => g.license?.status === "approved" && g.id?.status === "approved"
        );
      } else if (filter === "rejected") {
        rows = rows.filter(
          (g) => g.license?.status === "rejected" || g.id?.status === "rejected"
        );
      }
      setGroups(rows);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const reviewOne = async (kind, doc, status, noteOverride) => {
    const id = kind === "license" ? doc.license_document_id : doc.id_document_id;
    const path = kind === "license" ? "licenses" : "identity";
    const note = noteOverride !== undefined ? noteOverride : (notesByUser[doc.user_id] || null);
    await api.post(`/${path}/${id}/review`, { status, notes: note || null });
  };

  const handleSingle = async (kind, doc, status) => {
    const key = `${kind}-${doc.user_id}`;
    setBusyKey(key);
    try {
      await reviewOne(kind, doc, status);
      toast.success(`${kind === "license" ? "License" : "Identity"} ${status}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyKey(null);
    }
  };

  const handleBatch = async (g, status) => {
    const key = `batch-${g.user_id}`;
    setBusyKey(key);
    try {
      const tasks = [];
      if (g.id?.status === "pending") tasks.push(reviewOne("id", g.id, status));
      if (g.license?.status === "pending") tasks.push(reviewOne("license", g.license, status));
      if (tasks.length === 0) {
        toast.message("Nothing pending to review");
        return;
      }
      await Promise.all(tasks);
      setNotesByUser((m) => ({ ...m, [g.user_id]: "" }));
      toast.success(`${tasks.length} document(s) ${status}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div data-testid="admin-verifications" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Doctor verification queue</div>
          <h2 className="font-display text-2xl font-semibold mt-1">ID + License review</h2>
          <p className="text-sm text-muted-foreground mt-1">
            One card per doctor — both documents grouped together for faster review.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { k: "pending", l: "Pending" },
            { k: "incomplete", l: "Incomplete" },
            { k: "approved", l: "Approved" },
            { k: "rejected", l: "Rejected" },
            { k: "all", l: "All" },
          ].map((f) => (
            <button
              key={f.k}
              data-testid={`ver-filter-${f.k}`}
              onClick={() => setFilter(f.k)}
              className={`px-4 h-9 text-sm border rounded-md whitespace-nowrap ${
                filter === f.k
                  ? "bg-foreground text-white border-foreground"
                  : "bg-white border-border text-muted-foreground"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="text-sm text-destructive">{error}</div>}

      {groups.length === 0 ? (
        <div className="border border-border bg-white p-8 text-sm text-muted-foreground text-center">
          No doctors in this filter.
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => {
            const fullyApproved =
              g.license?.status === "approved" && g.id?.status === "approved";
            const anyPending =
              g.id?.status === "pending" || g.license?.status === "pending";
            const batchKey = `batch-${g.user_id}`;
            return (
              <div
                key={g.user_id}
                data-testid={`verification-${g.user_id}`}
                className="border border-border bg-white p-4 sm:p-5"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-display text-lg font-semibold">{g.user_name}</div>
                    <div className="text-xs text-muted-foreground break-all">{g.user_email}</div>
                    <div className="font-mono text-[10px] text-muted-foreground mt-1">
                      {g.user_id}
                    </div>
                  </div>
                  {fullyApproved && (
                    <div className="inline-flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-md">
                      <CheckCircle2 className="w-4 h-4" /> Fully verified
                    </div>
                  )}
                </div>

                <div className="mt-4 border-t border-b border-border divide-y divide-border">
                  <DocumentRow
                    kind="id"
                    doc={g.id}
                    onReview={handleSingle}
                    busy={busyKey?.startsWith(`id-${g.user_id}`) || busyKey === batchKey}
                  />
                  <DocumentRow
                    kind="license"
                    doc={g.license}
                    onReview={handleSingle}
                    busy={busyKey?.startsWith(`license-${g.user_id}`) || busyKey === batchKey}
                  />
                </div>

                {anyPending && (
                  <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-2 items-stretch">
                    <Input
                      placeholder="Notes (optional — applies to bulk action)"
                      value={notesByUser[g.user_id] || ""}
                      onChange={(e) =>
                        setNotesByUser((m) => ({ ...m, [g.user_id]: e.target.value }))
                      }
                      className="h-9 text-xs"
                      data-testid={`notes-${g.user_id}`}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 rounded-md flex-1 sm:flex-none"
                        onClick={() => handleBatch(g, "approved")}
                        disabled={busyKey === batchKey}
                        data-testid={`approve-all-${g.user_id}`}
                      >
                        Approve {g.id?.status === "pending" && g.license?.status === "pending" ? "both" : "pending"}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="flex-1 sm:flex-none"
                        onClick={() => handleBatch(g, "rejected")}
                        disabled={busyKey === batchKey}
                        data-testid={`reject-all-${g.user_id}`}
                      >
                        Reject pending
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
